import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { MrfiktivInvoiceLineViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { MrfiktivInvoiceLineDtoGen } from "./../services/mrfiktiv/v1/data-contracts";
import { AccountingTypeEnum, IProduct, Product } from "./product.entity";
import { TaxCategoryEnum } from "@/lib/enum/tax-category.enum";

/**
 * Invoice unit enum.
 * This is different to the UnitEnum as this should be able to be mapped to the XInvoice standard.
 */
export enum InvoiceUnitEnum {
  /**
   * The UNECE recommendations 20 and 21 include several codes for ‘piece’ or ‘unit’
   * •	C62 (one; unit)
   * •	H87 (piece)
   * •	NAR (number of articles)
   * •	XPP (piece)
   * @see https://wiki.idempiere.org/de/Ma%C3%9Feinheiten
   */
  PIECE = "piece"
}

@IsFilterable
class InvoiceLineBase implements MrfiktivInvoiceLineViewModelGen {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.productId"
  })
  productId?: string | null;

  /** Title of the invoice line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceLine.title"
  })
  title: string;

  /** Quantity of the item */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceLine.quantity"
  })
  quantity: number;

  /** Unit of the item */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceLine.unit"
  })
  unit: string = InvoiceUnitEnum.PIECE;

  /** Amount per unit */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceLine.amount"
  })
  amount: number;

  /** Account associated with the line */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceLine.account"
  })
  account?: number;

  /** Tax rate applied to the line */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoiceLine.taxRate"
  })
  taxRate: number;

  /** Description of the line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceLine.description"
  })
  description?: string;

  /** External ID of the line */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceLine.externalId"
  })
  externalId?: string | null;

  /** VAT category code of the line (BT-151, UNTDID 5305) */
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoiceLine.taxCategory"
  })
  taxCategory?: TaxCategoryEnum | null;

  /** VAT exemption reason text (BT-120) */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceLine.taxExemptionReason"
  })
  taxExemptionReason?: string | null;

  /** VAT exemption reason code (BT-121, VATEX) */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoiceLine.taxExemptionReasonCode"
  })
  taxExemptionReasonCode?: string | null;

  product?: IProduct;

  constructor(data?: Partial<MrfiktivInvoiceLineViewModelGen | IInvoiceLine>) {
    this.title = data?.title || "";
    this.description = data?.description;
    this.quantity = data?.quantity || 1;
    this.unit = data?.unit || InvoiceUnitEnum.PIECE;
    this.taxRate = data?.taxRate || 0;
    this.account = data?.account;
    this.amount = data?.amount ?? 0;
    this.externalId = data?.externalId;
    this.productId = data?.productId;
    this.taxCategory = data?.taxCategory as TaxCategoryEnum;
    this.taxExemptionReason = data?.taxExemptionReason;
    this.taxExemptionReasonCode = data?.taxExemptionReasonCode;

    if (data?.product) {
      this.product = new Product(data.product);
    }
  }

  get dto(): MrfiktivInvoiceLineDtoGen {
    return {
      account: this.account,
      amount: this.amount ?? 0,
      quantity: this.quantity,
      title: this.title,
      unit: this.unit,
      externalId: this.externalId,
      productId: this.productId,
      description: this.description,
      taxRate: this.taxRate,
      taxCategory: this.taxCategory,
      taxExemptionReason: this.taxExemptionReason,
      taxExemptionReasonCode: this.taxExemptionReasonCode
    };
  }

  /**
   * Line total excluding tax (BT-131).
   *
   * The `?? 0` needs its parentheses: `??` binds looser than `*`, so the unparenthesised
   * `this.amount ?? 0 * this.quantity` returned `this.amount` whenever it was set and dropped the
   * quantity and the rate entirely. All three getters read the same way round as their
   * Invoice-level namesakes: net excludes tax, gross includes it.
   */
  get totalNet() {
    return (this.amount ?? 0) * this.quantity;
  }

  /** Line total including tax. */
  get totalGross() {
    return (this.amount ?? 0) * this.quantity * (1 + this.taxRate / 100);
  }

  /** Tax charged on the line. */
  get tax() {
    return (this.amount ?? 0) * this.quantity * (this.taxRate / 100);
  }

  /**
   * Whether the net price carries a minus. EN 16931 BR-27 forbids a negative item net price
   * (BT-146), so a deduction has to be expressed the other way round — see
   * {@link moveAmountSignToQuantity}.
   */
  get hasNegativeAmount(): boolean {
    return this.amount < 0;
  }

  /**
   * Rewrites a deduction entered as a negative amount into the shape an X-Invoice accepts: a
   * positive net price with a negative quantity (BT-129, whose sign no rule constrains).
   *
   * Negates both factors rather than only the amount, so `amount * quantity` — and with it the
   * line total, the document total and the DATEV booking record, which reads the product before
   * the sign — comes out unchanged. A quantity of 0 would leave the sign nowhere to go and is
   * read as the 1 it defaults to.
   */
  moveAmountSignToQuantity(): void {
    if (!this.hasNegativeAmount) {
      return;
    }

    const quantity = this.quantity || 1;

    this.amount = -this.amount;
    this.quantity = -quantity;
  }

  setFromProduct(product?: IProduct, accountingType?: AccountingTypeEnum) {
    if (!product) {
      return;
    }

    this.title = product?.title;
    this.description = product?.description;
    this.unit = product?.unit ?? InvoiceUnitEnum.PIECE;
    this.taxRate = product?.taxRate ?? 0;
    // Preserve (not `?? 0`-style overwrite): a product with no category shouldn't erase one
    // already set on the line, e.g. by the seller-classification prefill in LineItemsForm/
    // InvoiceFormLinesTableRow/InvoiceFormPreview's addInvoiceLine().
    this.taxCategory = product?.taxCategory ?? this.taxCategory;
    this.taxExemptionReason = product?.taxExemptionReason ?? this.taxExemptionReason;
    this.taxExemptionReasonCode = product?.taxExemptionReasonCode ?? this.taxExemptionReasonCode;
    this.amount = product?.amount ?? 0;
    this.productId = product?.id;
    this.externalId = product?.externalId;
    this.quantity = 1;

    if (accountingType) {
      this.account = accountingType === AccountingTypeEnum.EXPENSE ? product.expenseAccount : product.revenueAccount;
    }
  }
}

type IInvoiceLine = InvoiceLineBase;
const InvoiceLine = Filter.createForClass(InvoiceLineBase);

export { IInvoiceLine, InvoiceLine };
